import { BHAVA_HOUSES, parseBhavaKarakPlanets } from '../../data/jyotishaBhavaHouses';
import { resolveGrahaLabel } from '../../data/jyotishaGrahaNames';
import { createNorthIndianChartModel, createPlanet } from './helpers';
import { NORTH_INDIAN_HOUSE_MAP } from './northIndianChartLayout';

export const BHAVA_CHART_TITLES_HI = {
  1: 'तनु',
  2: 'धन',
  3: 'सहज',
  4: 'बन्धु',
  5: 'पुत्र',
  6: 'अरि',
  7: 'युवति',
  8: 'रन्ध्र',
  9: 'धर्म',
  10: 'कर्म',
  11: 'लाभ',
  12: 'व्यय',
};

export const BHAVA_CHART_TITLES_EN = {
  1: 'Tanu',
  2: 'Dhana',
  3: 'Sahaja',
  4: 'Bandhu',
  5: 'Putra',
  6: 'Ari',
  7: 'Yuvati',
  8: 'Randhra',
  9: 'Dharma',
  10: 'Karma',
  11: 'Labha',
  12: 'Vyaya',
};

/** Secondary line under each bhava title on overview charts (natural-sign lord labels). */
export const BHAVA_CHART_LORD_TITLES_EN = {
  1: 'Tanu Lord',
  2: 'Dhana Lord',
  3: 'Sahaja Lord',
  4: 'Bandhu Lord',
  5: 'Putra Lord',
  6: 'Ari Lord',
  7: 'Yuvati Lord',
  8: 'Randhra Lord',
  9: 'Dharma Lord',
  10: 'Karma Lord',
  11: 'Labha Lord',
  12: 'Vyaya Lord',
};

export const BHAVA_CHART_KARAKA_TITLES_EN = {
  1: 'Tanu Karaka',
  2: 'Dhana Karaka',
  3: 'Sahaja Karaka',
  4: 'Bandhu Karaka',
  5: 'Putra Karaka',
  6: 'Ari Karaka',
  7: 'Yuvati Karaka',
  8: 'Randhra Karaka',
  9: 'Dharma Karaka',
  10: 'Karma Karaka',
  11: 'Labha Karaka',
  12: 'Vyaya Karaka',
};

export function createBhavaLordPlanetsByHouse() {
  return Object.fromEntries(
    BHAVA_HOUSES.map((bhava) => {
      const graha = resolveGrahaLabel(bhava.rulerEn);
      const hi = graha?.hi ?? bhava.rulerHi;
      const en = graha?.en ?? bhava.rulerEn;

      return [
        bhava.num,
        [
          createPlanet(hi, {
            id: `bhava-lord-${bhava.num}`,
            label: hi,
            hi,
            en,
            role: 'lord',
          }),
        ],
      ];
    })
  );
}

export function createBhavaKarakaPlanetsByHouse() {
  return Object.fromEntries(
    BHAVA_HOUSES.map((bhava) => {
      const karakKeys = parseBhavaKarakPlanets(bhava.karakEn);

      return [
        bhava.num,
        karakKeys.map((en, index) => {
          const graha = resolveGrahaLabel(en);
          const hi = graha?.hi ?? en;

          return createPlanet(hi, {
            id: `bhava-karaka-${bhava.num}-${index}`,
            label: hi,
            hi,
            en: graha?.en ?? en,
            role: 'karaka',
          });
        }),
      ];
    })
  );
}

export const BHAVA_CHART_TITLE_LAYOUTS = {
  1: { x: 50, y: 19.5, emphasized: true },
  2: { x: 24, y: 13.5, emphasized: false },
  3: { x: 13, y: 29.2, emphasized: false },
  4: { x: 25, y: 45.5, emphasized: false },
  5: { x: 13, y: 70.8, emphasized: false },
  6: { x: 25, y: 86.5, emphasized: false },
  7: { x: 50, y: 74.2, emphasized: true },
  8: { x: 75, y: 86.5, emphasized: false },
  9: { x: 87, y: 70.8, emphasized: false },
  10: { x: 75, y: 45.5, emphasized: false },
  11: { x: 87, y: 29.2, emphasized: false },
  12: { x: 76, y: 13.5, emphasized: false },
};

export const BHAVA_CHART_DETAIL_LAYOUTS = {
  1: { x: 50, y: 27, maxLines: 3, fontSize: 1.72, lineHeight: 3.35 },
  2: { x: 24, y: 18.4, maxLines: 2, fontSize: 1.46, lineHeight: 3.05 },
  3: { x: 13, y: 34.2, maxLines: 2, fontSize: 1.4, lineHeight: 2.95 },
  4: { x: 25, y: 52.4, maxLines: 3, fontSize: 1.56, lineHeight: 3.15 },
  5: { x: 13, y: 75.8, maxLines: 2, fontSize: 1.4, lineHeight: 2.95 },
  6: { x: 25, y: 91.4, maxLines: 2, fontSize: 1.4, lineHeight: 2.85 },
  7: { x: 50, y: 81.2, maxLines: 3, fontSize: 1.68, lineHeight: 3.2 },
  8: { x: 75, y: 91.4, maxLines: 2, fontSize: 1.4, lineHeight: 2.85 },
  9: { x: 87, y: 75.8, maxLines: 2, fontSize: 1.4, lineHeight: 2.95 },
  10: { x: 75, y: 52.4, maxLines: 3, fontSize: 1.56, lineHeight: 3.15 },
  11: { x: 87, y: 34.2, maxLines: 2, fontSize: 1.4, lineHeight: 2.95 },
  12: { x: 76, y: 18.4, maxLines: 2, fontSize: 1.46, lineHeight: 3.05 },
};

export const BHAVA_CHART_PLANET_ANCHORS = Object.fromEntries(
  Array.from({ length: 12 }, (_, index) => {
    const house = index + 1;
    const anchor = NORTH_INDIAN_HOUSE_MAP[house]?.planets ?? { x: 50, y: 50 };
    return [house, { x: anchor.x, y: anchor.y }];
  })
);

export const BHAVA_CHART_PLANET_TEXT_FRAMES = {
  1: { x: 50, y: 31.5, maxWidth: 18.5, kind: 'wide' },
  2: { x: 24, y: 8.5, maxWidth: 13.5, kind: 'medium' },
  3: { x: 8.3, y: 25, maxWidth: 10.2, kind: 'narrow' },
  4: { x: 24.5, y: 60.5, maxWidth: 15.5, kind: 'wide' },
  5: { x: 8.3, y: 75, maxWidth: 10.2, kind: 'narrow' },
  6: { x: 25, y: 93.5, maxWidth: 12.8, kind: 'medium' },
  7: { x: 50, y: 67.5, maxWidth: 18.5, kind: 'wide' },
  8: { x: 75, y: 93.5, maxWidth: 12.8, kind: 'medium' },
  9: { x: 91.7, y: 75, maxWidth: 10.2, kind: 'narrow' },
  10: { x: 75.5, y: 60.5, maxWidth: 15.5, kind: 'wide' },
  11: { x: 91.7, y: 25, maxWidth: 10.2, kind: 'narrow' },
  12: { x: 76, y: 8.5, maxWidth: 12.8, kind: 'medium' },
};

export function getBhavaTitleMeta(house, { hasDetails = false } = {}) {
  const layout = BHAVA_CHART_TITLE_LAYOUTS[house] ?? BHAVA_CHART_TITLE_LAYOUTS[1];
  const titleSize = hasDetails ? (layout.emphasized ? 3.8 : 3.45) : layout.emphasized ? 4.15 : 3.8;

  return {
    titleX: layout.x,
    titleY: hasDetails ? layout.y : layout.y + 2.6,
    titleSize,
  };
}

export function createBhavaHouseMeta({
  detailVisibilityByHouse = {},
  includePlanetAnchors: _includePlanetAnchors = true,
  overrides = {},
} = {}) {
  return Object.fromEntries(
    Array.from({ length: 12 }, (_, index) => {
      const house = index + 1;
      const baseMeta = {
        ...getBhavaTitleMeta(house, { hasDetails: Boolean(detailVisibilityByHouse[house]) }),
      };

      return [house, { ...baseMeta, ...(overrides[house] ?? {}) }];
    })
  );
}

export function createBhavaChartTheme({ isDark = false } = {}) {
  return isDark
    ? {
        background: 'rgba(2, 6, 23, 0.72)',
        houseFill: 'transparent',
        houseFillActive: 'rgba(251, 191, 36, 0.3)',
        houseGlowActive: 'rgba(251, 191, 36, 0.95)',
        houseFillHighlighted: 'transparent',
        houseFillActiveHighlighted: 'rgba(251, 191, 36, 0.3)',
        chartLine: 'rgba(226, 232, 240, 0.8)',
        title: '#f8fafc',
        secondaryTitle: '#cbd5e1',
        number: '#93c5fd',
        sign: '#f9a8d4',
        planetFill: '#0f172a',
        planetStroke: '#e2e8f0',
        planetText: '#f8fafc',
        subtitle: '#cbd5e1',
        directionPrimary: '#fcd34d',
        directionEn: '#fde68a',
      }
    : {
        background: '#ffffff',
        houseFill: 'transparent',
        houseFillActive: 'rgba(245, 158, 11, 0.22)',
        houseGlowActive: 'rgba(245, 158, 11, 0.9)',
        houseFillHighlighted: 'transparent',
        houseFillActiveHighlighted: 'rgba(245, 158, 11, 0.22)',
        chartLine: '#111827',
        title: '#111827',
        secondaryTitle: '#334155',
        number: '#1d4ed8',
        sign: '#7c3aed',
        planetFill: '#f8fafc',
        planetStroke: '#0f172a',
        planetText: '#0f172a',
        subtitle: '#475569',
        directionPrimary: '#b45309',
        directionEn: '#92400e',
      };
}

export function getBhavaTextPlanetSlots(house, total) {
  const frame = BHAVA_CHART_PLANET_TEXT_FRAMES[house] ?? BHAVA_CHART_PLANET_TEXT_FRAMES[1];
  const centroid = NORTH_INDIAN_HOUSE_MAP[house]?.planets ?? { x: frame.x, y: frame.y };
  const { kind } = frame;
  const x = centroid.x;
  const y = centroid.y;

  if (total <= 1) {
    return [{ x, y }];
  }

  if (kind === 'narrow') {
    if (total === 2) return [{ x, y: y - 2.3 }, { x, y: y + 2.3 }];
    if (total === 3) return [{ x, y: y - 3.2 }, { x, y }, { x, y: y + 3.2 }];
    if (total === 4) return [{ x: x - 1.6, y: y - 2.4 }, { x: x + 1.6, y: y - 2.4 }, { x: x - 1.6, y: y + 2.4 }, { x: x + 1.6, y: y + 2.4 }];
    return Array.from({ length: total }, (_, index) => ({ x, y: y - ((total - 1) * 1.45) / 2 + index * 2.9 }));
  }

  if (kind === 'medium') {
    if (total === 2) return [{ x, y: y - 2.4 }, { x, y: y + 2.4 }];
    if (total === 3) return [{ x, y: y - 3 }, { x: x - 2.4, y: y + 2.3 }, { x: x + 2.4, y: y + 2.3 }];
    if (total === 4) return [{ x: x - 2.5, y: y - 2.5 }, { x: x + 2.5, y: y - 2.5 }, { x: x - 2.5, y: y + 2.5 }, { x: x + 2.5, y: y + 2.5 }];
    const columns = 2;
    const rows = Math.ceil(total / columns);
    const gapX = 2.4;
    const gapY = 2.7;
    const startX = x - ((columns - 1) * gapX) / 2;
    const startY = y - ((rows - 1) * gapY) / 2;
    return Array.from({ length: total }, (_, index) => ({
      x: startX + (index % columns) * gapX,
      y: startY + Math.floor(index / columns) * gapY,
    }));
  }

  if (total === 2) return [{ x: x - 3.1, y }, { x: x + 3.1, y }];
  if (total === 3) return [{ x, y: y - 2.6 }, { x: x - 3.2, y: y + 2.2 }, { x: x + 3.2, y: y + 2.2 }];
  if (total === 4) return [{ x: x - 3.6, y: y - 2.3 }, { x: x + 3.6, y: y - 2.3 }, { x: x - 3.6, y: y + 2.3 }, { x: x + 3.6, y: y + 2.3 }];

  const columns = total <= 6 ? 2 : 3;
  const rows = Math.ceil(total / columns);
  const gapX = columns === 2 ? 3.2 : 2.6;
  const gapY = 2.7;
  const startX = x - ((columns - 1) * gapX) / 2;
  const startY = y - ((rows - 1) * gapY) / 2;

  return Array.from({ length: total }, (_, index) => ({
    x: startX + (index % columns) * gapX,
    y: startY + Math.floor(index / columns) * gapY,
  }));
}

export function createBhavaChartData({
  selectedHouse = null,
  lagnaNumber = null,
  planetsByHouse = {},
  highlightedHouses = [],
  showEnglishTitles = false,
  titles = BHAVA_CHART_TITLES_HI,
  secondaryTitles = null,
  subtitles = {},
  metadataByHouse = {},
  houseMeta = {},
  numberLabels = null,
} = {}) {
  return createNorthIndianChartModel({
    startHouseNumber: Array.isArray(numberLabels) ? null : lagnaNumber,
    selectedHouse,
    highlightedHouses,
    planetsByHouse,
    titles,
    secondaryTitles: secondaryTitles ?? (showEnglishTitles ? BHAVA_CHART_TITLES_EN : {}),
    subtitles,
    metadataByHouse,
    houseMeta,
    numberLabels: Array.isArray(numberLabels) ? numberLabels : null,
  });
}
